import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  PauseIcon,
  ScissorsIcon,
  TrashIcon,
  ArrowPathIcon,
  FilmIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  SquaresPlusIcon,
  ArrowDownTrayIcon,
  SparklesIcon,
  SwatchIcon,
  PhotoIcon,
  ArrowsPointingOutIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  MinusIcon,
  ChatBubbleBottomCenterTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpTrayIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import videoEditorApi, { IVideoEditProject, IVideoSegment, IVideoTrack, StockSearchResult } from '@/services/videoEditorApi';
import { projectMediaApi, ProjectMediaItem } from '@/services/projectMediaApi';
import api from '@/services/api';
import { useProject } from '@/hooks/useProject';
import EditorTimeline from './EditorTimeline';

function formatTime(seconds: number): string {
  const s = Math.max(0, seconds);
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 100);
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
  }
  return `${mins}:${String(secs).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
}

export default function VideoEditorPage() {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const { selectedProject } = useProject();

  const [project, setProject] = useState<IVideoEditProject | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rendering, setRendering] = useState(false);

  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  const [, setSelectedTrackId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [tab, setTab] = useState<'tools' | 'trim' | 'crop' | 'ai' | 'stock' | 'layers'>('tools');
  const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);
  const [timelineAudioSources, setTimelineAudioSources] = useState<Record<string, string>>({});
  const [videoLoading, setVideoLoading] = useState(false);
  const [mediaDuration, setMediaDuration] = useState(0);

  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);

  const [projectMedia, setProjectMedia] = useState<ProjectMediaItem[]>([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoSearch, setVideoSearch] = useState('');

  const [projectAudio, setProjectAudio] = useState<ProjectMediaItem[]>([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);
  const [audioSearch, setAudioSearch] = useState('');
  const [projectImages, setProjectImages] = useState<ProjectMediaItem[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [captionText, setCaptionText] = useState('');
  const [captionDuration, setCaptionDuration] = useState(4);
  const [overlayDuration, setOverlayDuration] = useState(5);

  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineAudioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const [naturalSize, setNaturalSize] = useState({ w: 1920, h: 1080 });
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, w: 1920, h: 1080 });
  const [cropDrag, setCropDrag] = useState<'move' | 'resize' | null>(null);
  const cropStartRef = useRef({ x: 0, y: 0, rect: { x: 0, y: 0, w: 0, h: 0 } });

  const [, setStockOpen] = useState(false);
  const [stockType, setStockType] = useState<'video' | 'music'>('video');
  const [stockQuery, setStockQuery] = useState('');
  const [stockResults, setStockResults] = useState<StockSearchResult[]>([]);
  const [stockLoading, setStockLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioFileInputRef = useRef<HTMLInputElement>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const watermarkFileInputRef = useRef<HTMLInputElement>(null);

  const loadProjectMedia = async () => {
    if (!selectedProject?._id) return;
    setMediaLoading(true);
    setAudioLoading(true);
    try {
      const [videoRes, audioRes, imageRes] = await Promise.all([
        projectMediaApi.getMedia('videos', { projectId: selectedProject._id }),
        projectMediaApi.getMedia('audio', { projectId: selectedProject._id }),
        projectMediaApi.getMedia('images', { projectId: selectedProject._id }),
      ]);
      if (videoRes.success) setProjectMedia(videoRes.data.items);
      if (audioRes.success) setProjectAudio(audioRes.data.items);
      if (imageRes.success) setProjectImages(imageRes.data.items);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load project media');
    } finally {
      setMediaLoading(false);
      setAudioLoading(false);
    }
  };

  const filteredProjectMedia = useMemo(() => {
    const term = videoSearch.trim().toLowerCase();
    return projectMedia
      .filter((item) => !term || item.name.toLowerCase().includes(term))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [projectMedia, videoSearch]);

  const filteredProjectAudio = useMemo(() => {
    const term = audioSearch.trim().toLowerCase();
    return projectAudio
      .filter((item) => !term || item.name.toLowerCase().includes(term))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);
  }, [projectAudio, audioSearch]);

  useEffect(() => {
    loadProjectMedia();
    if (routeProjectId) {
      loadEditorProject(routeProjectId);
    } else {
      setProject(null);
    }
  }, [selectedProject?._id, routeProjectId]);

  const loadEditorProject = async (editorProjectId: string) => {
    setLoading(true);
    try {
      const res = await videoEditorApi.getProject(editorProjectId);
      if (res.data.success) {
        const p = res.data.project;
        setProject(p);
        const firstVideoTrack = p.timeline.tracks.find((t) => t.type === 'video');
        const firstSeg = firstVideoTrack?.segments[0];
        if (firstSeg) {
          setSelectedSegmentId(firstSeg.segmentId);
          setSelectedTrackId(firstVideoTrack!.trackId);
        } else {
          setSelectedSegmentId(null);
          setSelectedTrackId(null);
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const updateDisplaySize = () => {
    const video = videoRef.current;
    if (!video) return;
    const rect = video.getBoundingClientRect();
    setDisplaySize({ w: rect.width, h: rect.height });
  };

  useEffect(() => {
    updateDisplaySize();
    const onResize = () => updateDisplaySize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const selectedSegment = useMemo(() => {
    if (!project) return null;
    if (selectedSegmentId) {
      for (const track of project.timeline.tracks) {
        const seg = track.segments.find((segment) => segment.segmentId === selectedSegmentId);
        if (seg) return { seg, track };
      }
    }
    for (const track of project.timeline.tracks) {
      const seg = track.segments.find((segment) => currentTime >= segment.timelineStart && currentTime <= segment.timelineEnd);
      if (seg) return { seg, track };
    }
    for (const track of project.timeline.tracks) {
      if (track.segments[0]) return { seg: track.segments[0], track };
    }
    return null;
  }, [project, selectedSegmentId, currentTime]);

  useEffect(() => {
    if (selectedSegment) {
      setTrimStart(selectedSegment.seg.startTime);
      setTrimEnd(selectedSegment.seg.endTime);
    }
  }, [selectedSegment?.seg.segmentId]);

  const cropTargetSegment = useMemo(() => {
    if (!project) return null;
    if (selectedSegment?.track.type === 'video') return selectedSegment.seg;
    const videoTrack = project.timeline.tracks.find((track) => track.type === 'video');
    return videoTrack?.segments.find((segment) => currentTime >= segment.timelineStart && currentTime <= segment.timelineEnd) || videoTrack?.segments[0] || null;
  }, [project, selectedSegment, currentTime]);

  useEffect(() => {
    if (tab !== 'crop' || !cropTargetSegment) return;
    const existingCrop = [...(cropTargetSegment.effects || [])].reverse().find((effect) => effect.type === 'crop')?.params?.crop;
    if (existingCrop) {
      setCropRect({ x: existingCrop.x, y: existingCrop.y, w: existingCrop.w, h: existingCrop.h });
    } else {
      setCropRect({ x: 0, y: 0, w: naturalSize.w, h: naturalSize.h });
    }
  }, [tab, cropTargetSegment?.segmentId]);

  const appliedPreviewCrop = useMemo(() => {
    if (!cropTargetSegment || tab === 'crop') return null;
    return [...(cropTargetSegment.effects || [])].reverse().find((effect) => effect.type === 'crop')?.params?.crop || null;
  }, [cropTargetSegment, tab]);

  const previewClipPath = appliedPreviewCrop
    ? `inset(${Math.max(0, appliedPreviewCrop.y / naturalSize.h * 100)}% ${Math.max(0, (naturalSize.w - appliedPreviewCrop.x - appliedPreviewCrop.w) / naturalSize.w * 100)}% ${Math.max(0, (naturalSize.h - appliedPreviewCrop.y - appliedPreviewCrop.h) / naturalSize.h * 100)}% ${Math.max(0, appliedPreviewCrop.x / naturalSize.w * 100)}%)`
    : undefined;

  const timelineAudioSegments = useMemo(() => {
    if (!project) return [];
    return project.timeline.tracks
      .filter((track) => track.type === 'audio' && track.trackId !== 'audio-main' && !track.muted)
      .flatMap((track) => track.segments);
  }, [project]);

  const audioSegmentSignature = useMemo(
    () => timelineAudioSegments.map((segment) => `${segment.segmentId}:${segment.sourceKey || segment.s3Url || ''}`).join('|'),
    [timelineAudioSegments]
  );

  const activeLayers = useMemo(() => {
    if (!project) return [];
    return project.timeline.tracks
      .filter((track) => track.type === 'caption' || track.type === 'overlay')
      .flatMap((track) => track.segments.map((segment) => ({ track, segment })))
      .filter(({ segment }) => currentTime >= segment.timelineStart && currentTime <= segment.timelineEnd)
      .sort((a, b) => a.segment.layer - b.segment.layer);
  }, [project, currentTime]);

  const totalDuration = useMemo(() => {
    if (!project) return 1;
    const max = project.timeline.tracks.reduce((acc, track) => {
      return Math.max(acc, ...track.segments.map((s) => s.timelineEnd));
    }, Math.max(project.durationSeconds || 0, mediaDuration));
    return Math.max(1, max);
  }, [project, mediaDuration]);

  const videoSrc = project?.previewUrl || project?.sourceVideoS3Url || '';
  const mainVideoSegmentId = project?.timeline.tracks.find((track) => track.type === 'video')?.segments[0]?.segmentId;

  useEffect(() => {
    if (!project?.projectId || timelineAudioSegments.length === 0) {
      setTimelineAudioSources({});
      return;
    }
    let cancelled = false;
    const objectUrls: string[] = [];
    const loadAudio = async () => {
      const entries = await Promise.all(timelineAudioSegments.map(async (segment) => {
        try {
          const response = await api.get(`/video-editor/${project.projectId}/segments/${segment.segmentId}/media`, { responseType: 'blob' });
          if (cancelled) return null;
          const objectUrl = URL.createObjectURL(response.data as Blob);
          objectUrls.push(objectUrl);
          return [segment.segmentId, objectUrl] as const;
        } catch {
          return null;
        }
      }));
      if (!cancelled) setTimelineAudioSources(Object.fromEntries(entries.filter((entry): entry is readonly [string, string] => Boolean(entry))));
    };
    loadAudio();
    return () => {
      cancelled = true;
      Object.values(timelineAudioRefs.current).forEach((audio) => audio?.pause());
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [project?.projectId, audioSegmentSignature]);

  useEffect(() => {
    if (!videoSrc) {
      setVideoObjectUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadVideo = async () => {
      setVideoLoading(true);
      if (project?.projectId && mainVideoSegmentId) {
        try {
          const response = await api.get(`/video-editor/${project.projectId}/segments/${mainVideoSegmentId}/media`, { responseType: 'blob' });
          if (cancelled) return;
          objectUrl = URL.createObjectURL(response.data as Blob);
          setVideoObjectUrl(objectUrl);
          setVideoLoading(false);
          return;
        } catch {
          if (cancelled) return;
        }
      }
      // Direct S3 / public URLs can play without auth
      if (videoSrc.startsWith('https://') || videoSrc.startsWith('http://')) {
        setVideoObjectUrl(videoSrc);
        return;
      }
      // Relative /api URLs require authentication, fetch as blob
      setVideoLoading(true);
      try {
        const res = await api.get(videoSrc.replace(/^\/api/, ''), { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setVideoObjectUrl(objectUrl);
      } catch (err: any) {
        if (!cancelled) toast.error('Failed to load video preview');
      } finally {
        if (!cancelled) setVideoLoading(false);
      }
    };

    loadVideo();
    return () => {
      cancelled = true;
      if (objectUrl && objectUrl !== videoSrc) URL.revokeObjectURL(objectUrl);
    };
  }, [videoSrc, project?.projectId, mainVideoSegmentId]);

  const createEditorProject = async (title: string, sourceUrl: string) => {
    if (!selectedProject?._id) {
      toast.error('Please select a project from the top dropdown');
      return;
    }
    setCreating(true);
    try {
      const res = await videoEditorApi.createProject({
        title,
        sourceVideoS3Url: sourceUrl,
        parentProjectId: selectedProject._id,
      });
      if (res.data.success) {
        const createdProject = res.data.project;
        setProject(createdProject);
        const firstVideoTrack = createdProject.timeline.tracks.find((track) => track.type === 'video');
        const firstSegment = firstVideoTrack?.segments[0];
        setSelectedSegmentId(firstSegment?.segmentId || null);
        setSelectedTrackId(firstVideoTrack?.trackId || null);
        setCurrentTime(0);
        toast.success('Editor project created');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFromMedia = async (item: ProjectMediaItem) => {
    if (!item.url) {
      toast.error('Selected video does not have a playable URL');
      return;
    }
    await createEditorProject(item.name, item.url);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedProject?._id) {
      toast.error('Please select a project from the top dropdown');
      return;
    }

    setUploading(true);
    try {
      const uploadRes = await videoEditorApi.uploadVideo(file);
      if (uploadRes.data.success) {
        await createEditorProject(file.name, uploadRes.data.data.s3Url);
      } else {
        toast.error('Upload failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  const getAudioDuration = async (url: string): Promise<number> => {
    let objectUrl: string | null = null;
    try {
      let source = url;
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        const response = await api.get(url.replace(/^\/api/, ''), { responseType: 'blob' });
        objectUrl = URL.createObjectURL(response.data as Blob);
        source = objectUrl;
      }
      return await new Promise<number>((resolve) => {
        const audio = new Audio(source);
        audio.addEventListener('loadedmetadata', () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0), { once: true });
        audio.addEventListener('error', () => resolve(0), { once: true });
      });
    } catch {
      return 0;
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
  };

  const addAudioToTimeline = async (
    name: string,
    s3Url: string,
    previewUrl?: string,
    options?: { timelineStart?: number; forceNewTrack?: boolean }
  ) => {
    if (!project) {
      toast.error('Create or select an editor project first');
      return;
    }
    const sourceUrl = previewUrl || s3Url;
    const duration = await getAudioDuration(sourceUrl) || (project.durationSeconds || 60);
    const audioTracks = project.timeline.tracks.filter((track) => track.type === 'audio');
    const timelineStart = Math.max(0, options?.timelineStart ?? currentTime ?? 0);
    let targetTrack = options?.forceNewTrack
      ? undefined
      : audioTracks.find((track) => track.segments.every((segment) => segment.timelineEnd <= timelineStart || segment.timelineStart >= timelineStart + duration));
    if (!targetTrack) {
      targetTrack = {
        trackId: `audio-${crypto.randomUUID()}`,
        type: 'audio',
        name: `Audio ${audioTracks.length + 1}`,
        visible: true,
        muted: false,
        segments: [],
      };
    }

    const segmentId = crypto.randomUUID();
    const segment: IVideoSegment = {
      segmentId,
      trackId: targetTrack.trackId,
      sourceType: 'source',
      sourceKey: s3Url,
      s3Url: previewUrl || s3Url,
      previewUrl,
      title: name,
      startTime: 0,
      endTime: duration,
      timelineStart,
      timelineEnd: timelineStart + duration,
      layer: 0,
      effects: [],
    };
    const targetExists = project.timeline.tracks.some((track) => track.trackId === targetTrack.trackId);
    const tracks = targetExists
      ? project.timeline.tracks.map((track) => track.trackId === targetTrack.trackId ? { ...track, segments: [...track.segments, segment] } : { ...track, segments: [...track.segments] })
      : [...project.timeline.tracks.map((track) => ({ ...track, segments: [...track.segments] })), { ...targetTrack, segments: [segment] }];
    const previous = project;
    setProject({ ...project, timeline: { tracks } });
    setSelectedSegmentId(segmentId);
    setSelectedTrackId(targetTrack.trackId);

    try {
      const res = await videoEditorApi.updateTimeline(project.projectId, { tracks });
      if (res.data.success) {
        setProject(res.data.project);
        toast.success(options?.forceNewTrack ? 'New audio track added' : 'Audio added to timeline');
      }
    } catch (err: any) {
      setProject(previous);
      toast.error(err?.response?.data?.message || 'Failed to add audio');
    }
  };

  const handleAddAudioFromMedia = async (item: ProjectMediaItem, timelineStart?: number, forceNewTrack = false) => {
    if (!item.url) {
      toast.error('Selected audio does not have a playable URL');
      return;
    }
    await addAudioToTimeline(item.name, item.url, item.url, { timelineStart, forceNewTrack });
  };

  const handleAudioDrop = (mediaId: string, timelineStart: number) => {
    const item = projectAudio.find((audio) => audio.id === mediaId || audio._id === mediaId);
    if (!item) {
      toast.error('Audio file is no longer available');
      return;
    }
    handleAddAudioFromMedia(item, timelineStart, true);
  };

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedProject?._id) {
      toast.error('Please select a project from the top dropdown');
      return;
    }

    setAudioUploading(true);
    try {
      const uploadRes = await videoEditorApi.uploadAudio(file);
      if (uploadRes.data.success) {
        await addAudioToTimeline(file.name, uploadRes.data.data.s3Url, uploadRes.data.data.previewUrl);
      } else {
        toast.error('Upload failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to upload audio');
    } finally {
      setAudioUploading(false);
    }
  };

  const saveTimeline = async (tracks: IVideoTrack[], successMessage: string) => {
    if (!project) return;
    const previous = project;
    setProject({ ...project, timeline: { tracks } });
    try {
      const response = await videoEditorApi.updateTimeline(project.projectId, { tracks });
      if (response.data.success) {
        setProject(response.data.project);
        toast.success(successMessage);
      }
    } catch (err: any) {
      setProject(previous);
      toast.error(err?.response?.data?.message || 'Failed to update timeline');
    }
  };

  const handleAddAudioTrack = () => {
    if (!project) return;
    const number = project.timeline.tracks.filter((track) => track.type === 'audio').length + 1;
    const track: IVideoTrack = {
      trackId: `audio-${crypto.randomUUID()}`,
      type: 'audio',
      name: `Audio ${number}`,
      visible: true,
      muted: false,
      segments: [],
    };
    saveTimeline([...project.timeline.tracks, track], 'Audio track added');
  };

  const handleDeleteTrack = (trackId: string) => {
    if (!project) return;
    const track = project.timeline.tracks.find((item) => item.trackId === trackId);
    if (!track || track.type !== 'audio') return;
    const tracks = project.timeline.tracks.filter((item) => item.trackId !== trackId);
    if (track.segments.some((segment) => segment.segmentId === selectedSegmentId)) setSelectedSegmentId(null);
    saveTimeline(tracks, 'Audio track deleted');
  };

  const handleAddCaption = () => {
    if (!project || !captionText.trim()) {
      toast.error('Enter caption text first');
      return;
    }
    const trackId = `caption-${crypto.randomUUID()}`;
    const segmentId = crypto.randomUUID();
    const duration = Math.max(0.5, captionDuration);
    const segment: IVideoSegment = {
      segmentId,
      trackId,
      sourceType: 'source',
      title: captionText.trim(),
      startTime: 0,
      endTime: duration,
      timelineStart: currentTime,
      timelineEnd: currentTime + duration,
      layer: 10,
      effects: [{ effectId: crypto.randomUUID(), type: 'subtitle', enabled: true, params: { text: captionText.trim(), position: 'bottom', fontSize: 42, color: '#ffffff' } }],
    };
    const existing = project.timeline.tracks.find((track) => track.type === 'caption');
    const tracks = existing
      ? project.timeline.tracks.map((track) => track.trackId === existing.trackId ? { ...track, segments: [...track.segments, { ...segment, trackId: track.trackId }] } : track)
      : [...project.timeline.tracks, { trackId, type: 'caption' as const, name: 'Captions', visible: true, muted: false, segments: [segment] }];
    setSelectedSegmentId(segmentId);
    setTab('layers');
    setCaptionText('');
    saveTimeline(tracks, 'Caption added');
  };

  const addImageLayer = (name: string, s3Url: string, previewUrl: string, kind: 'image' | 'watermark') => {
    if (!project) return;
    const existing = project.timeline.tracks.find((track) => track.type === 'overlay' && track.name === (kind === 'watermark' ? 'Watermarks' : 'Images'));
    const trackId = existing?.trackId || `${kind}-${crypto.randomUUID()}`;
    const segmentId = crypto.randomUUID();
    const duration = Math.max(0.5, overlayDuration);
    const segment: IVideoSegment = {
      segmentId,
      trackId,
      sourceType: 'broll',
      sourceKey: s3Url,
      s3Url,
      previewUrl,
      title: name,
      startTime: 0,
      endTime: duration,
      timelineStart: currentTime,
      timelineEnd: currentTime + duration,
      layer: kind === 'watermark' ? 20 : 5,
      effects: [{ effectId: crypto.randomUUID(), type: 'broll', enabled: true, params: { kind, position: kind === 'watermark' ? 'top-right' : 'center', opacity: kind === 'watermark' ? 0.75 : 1, scale: kind === 'watermark' ? 0.18 : 0.4 } }],
    };
    const tracks = existing
      ? project.timeline.tracks.map((track) => track.trackId === existing.trackId ? { ...track, segments: [...track.segments, segment] } : track)
      : [...project.timeline.tracks, { trackId, type: 'overlay' as const, name: kind === 'watermark' ? 'Watermarks' : 'Images', visible: true, muted: false, segments: [segment] }];
    setSelectedSegmentId(segmentId);
    setTab('layers');
    saveTimeline(tracks, kind === 'watermark' ? 'Watermark added' : 'Image added');
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, kind: 'image' | 'watermark') => {
    const file = event.target.files?.[0];
    if (!file || !project) return;
    setImageUploading(true);
    try {
      const response = await videoEditorApi.uploadImage(file);
      if (response.data.success) addImageLayer(file.name, response.data.data.s3Url, response.data.data.previewUrl, kind);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const refreshProject = async (projectId: string) => {
    try {
      const res = await videoEditorApi.getProject(projectId);
      if (res.data.success) {
        setProject(res.data.project);
        const stillExists = res.data.project.timeline.tracks.some((t) => t.segments.some((s) => s.segmentId === selectedSegmentId));
        if (!stillExists) {
          const firstVideoTrack = res.data.project.timeline.tracks.find((t) => t.type === 'video');
          const firstSeg = firstVideoTrack?.segments[0];
          setSelectedSegmentId(firstSeg ? firstSeg.segmentId : null);
          setSelectedTrackId(firstVideoTrack ? firstVideoTrack.trackId : null);
        }
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to refresh project');
    }
  };

  const applyOp = async (type: string, params: Record<string, any> = {}, successMsg?: string) => {
    if (!project) return;
    const toastId = toast.loading(`${type}...`);
    try {
      const res = await videoEditorApi.applyOperation(project.projectId, type, params);
      if (res.data.success && res.data.status === 'completed') {
        toast.success(successMsg || `${type} applied`, { id: toastId });
      } else if (res.data.error) {
        toast.error(`${type} failed: ${res.data.error}`, { id: toastId });
      } else {
        toast.dismiss(toastId);
      }
      await refreshProject(project.projectId);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || `Failed to apply ${type}`, { id: toastId });
    }
  };

  const syncTimelineAudio = (time: number, shouldPlay: boolean) => {
    timelineAudioSegments.forEach((segment) => {
      const audio = timelineAudioRefs.current[segment.segmentId];
      if (!audio) return;
      const active = time >= segment.timelineStart && time < segment.timelineEnd;
      if (!active) {
        if (!audio.paused) audio.pause();
        return;
      }
      const sourceTime = Math.max(segment.startTime, segment.startTime + time - segment.timelineStart);
      if (Math.abs(audio.currentTime - sourceTime) > 0.15) audio.currentTime = sourceTime;
      if (shouldPlay && audio.paused) audio.play().catch(() => undefined);
      if (!shouldPlay && !audio.paused) audio.pause();
    });
  };

  const seek = (time: number) => {
    const video = videoRef.current;
    if (!video) return;
    const safe = Math.max(0, Math.min(time, totalDuration));
    video.currentTime = safe;
    setCurrentTime(safe);
    syncTimelineAudio(safe, !video.paused);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video || !videoObjectUrl) return;
    if (video.paused || video.ended) {
      if (video.ended || video.currentTime >= video.duration) seek(0);
      video.play().catch(() => toast.error('Video cannot play'));
      return;
    }
    video.pause();
  };

  const stepTime = (delta: number) => seek(currentTime + delta);

  const handleSegmentMove = (segmentId: string, trackId: string, newTimelineStart: number) => {
    if (!project) return;
    const fps = project.fps || 30;
    const safeStart = Math.max(0, Math.round(newTimelineStart * fps) / fps);
    const tracks = project.timeline.tracks.map((track) => ({
      ...track,
      segments: track.segments.map((segment) => {
        if (track.trackId !== trackId || segment.segmentId !== segmentId) return { ...segment };
        const duration = segment.timelineEnd - segment.timelineStart;
        return { ...segment, timelineStart: safeStart, timelineEnd: safeStart + duration };
      }),
    }));
    setProject({ ...project, timeline: { tracks } });
    videoEditorApi
      .updateTimeline(project.projectId, { tracks })
      .then((response) => {
        if (response.data.success) setProject(response.data.project);
      })
      .catch((err: any) => {
        setProject(project);
        toast.error(err?.response?.data?.message || 'Failed to move segment');
      });
  };

  const handleSplit = () => {
    if (!selectedSegment) return;
    const { seg } = selectedSegment;
    if (currentTime <= seg.timelineStart || currentTime >= seg.timelineEnd) {
      toast.error('Move playhead inside the selected segment first');
      return;
    }
    applyOp('split', { segmentId: seg.segmentId, splitTime: currentTime });
  };

  const handleTrim = () => {
    if (!selectedSegment) return;
    const { seg } = selectedSegment;
    const newStart = Math.max(seg.startTime, Math.min(trimStart, trimEnd));
    const newEnd = Math.min(seg.endTime, Math.max(trimStart, trimEnd));
    if (newEnd - newStart < 0.1) {
      toast.error('Trim range too small');
      return;
    }
    applyOp('trim', { segmentId: seg.segmentId, start: newStart, end: newEnd }, 'Trimmed');
  };

  const handleDelete = () => {
    if (!selectedSegment) return;
    applyOp('rippleDelete', { segmentId: selectedSegment.seg.segmentId }, 'Deleted');
    setSelectedSegmentId(null);
  };

  const handleMerge = () => {
    if (!selectedSegment) return;
    const { seg, track } = selectedSegment;
    const idx = track.segments.findIndex((s) => s.segmentId === seg.segmentId);
    if (idx === -1 || idx >= track.segments.length - 1) {
      toast.error('Select a segment followed by another segment');
      return;
    }
    const next = track.segments[idx + 1];
    applyOp('merge', { segmentIds: [seg.segmentId, next.segmentId] }, 'Merged');
  };

  const handleEffect = (type: string, params: Record<string, any> = {}) => {
    if (!selectedSegment) return;
    applyOp(type, { segmentId: selectedSegment.seg.segmentId, params }, `${type} added`);
  };

  const handleCrop = async () => {
    if (!project || !cropTargetSegment) {
      toast.error('No video segment is available to crop');
      return;
    }
    const x = Math.max(0, Math.min(naturalSize.w - 2, Math.round(cropRect.x)));
    const y = Math.max(0, Math.min(naturalSize.h - 2, Math.round(cropRect.y)));
    const w = Math.max(2, Math.min(naturalSize.w - x, Math.round(cropRect.w)));
    const h = Math.max(2, Math.min(naturalSize.h - y, Math.round(cropRect.h)));
    setCropRect({ x, y, w, h });
    setSelectedSegmentId(cropTargetSegment.segmentId);
    await applyOp('crop', { segmentId: cropTargetSegment.segmentId, params: { crop: { x, y, w, h } } }, 'Crop applied');
    setTab('tools');
  };

  const resetCrop = () => {
    setCropRect({ x: 0, y: 0, w: naturalSize.w, h: naturalSize.h });
  };

  const handleRender = async () => {
    if (!project) return;
    setRendering(true);
    try {
      await videoEditorApi.renderProject(project.projectId);
      toast.success('Render started');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start render');
    } finally {
      setRendering(false);
    }
  };

  const searchStock = async (type: 'video' | 'music', q: string) => {
    setStockLoading(true);
    try {
      const res = type === 'video' ? await videoEditorApi.searchStockVideo(q) : await videoEditorApi.searchStockMusic(q);
      if (res.data.success) setStockResults(res.data.results);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Stock search failed');
    } finally {
      setStockLoading(false);
    }
  };

  const openStock = (type: 'video' | 'music') => {
    setStockType(type);
    setStockQuery(type === 'video' ? 'nature' : 'upbeat');
    setStockResults([]);
    setStockOpen(true);
    setTab('stock');
  };

  const addStockToTimeline = (item: StockSearchResult) => {
    if (!project) return;
    const videoTrack = project.timeline.tracks.find((t) => t.type === 'video');
    if (!videoTrack) return;
    const insertTime = currentTime;
    const newSegment: IVideoSegment = {
      segmentId: `stock-${Date.now()}`,
      trackId: videoTrack.trackId,
      sourceType: 'stock',
      sourceKey: item.downloadUrl || item.previewUrl || '',
      s3Url: item.downloadUrl || item.previewUrl,
      title: item.title,
      startTime: 0,
      endTime: item.duration || 5,
      timelineStart: insertTime,
      timelineEnd: insertTime + (item.duration || 5),
      layer: 1,
      effects: [],
    };
    const tracks = project.timeline.tracks.map((track) =>
      track.trackId === videoTrack.trackId ? { ...track, segments: [...track.segments, newSegment] } : { ...track, segments: [...track.segments] }
    );
    setProject({ ...project, timeline: { tracks } });
    setSelectedSegmentId(newSegment.segmentId);
    setSelectedTrackId(videoTrack.trackId);
    videoEditorApi
      .updateTimeline(project.projectId, { tracks })
      .then((response) => {
        if (response.data.success) setProject(response.data.project);
      })
      .catch((err: any) => {
        setProject(project);
        toast.error(err?.response?.data?.message || 'Failed to add stock');
      });
  };

  const handleKeyDown = (e: KeyboardEvent | React.KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    switch (e.key) {
      case ' ':
        e.preventDefault();
        togglePlay();
        break;
      case 's':
      case 'S':
        handleSplit();
        break;
      case 'Delete':
      case 'Backspace':
        handleDelete();
        break;
      case 'm':
      case 'M':
        handleMerge();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        stepTime(-1 / 30);
        break;
      case 'ArrowRight':
        e.preventDefault();
        stepTime(1 / 30);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const scaleX = displaySize.w > 0 && naturalSize.w > 0 ? displaySize.w / naturalSize.w : 1;
  const scaleY = displaySize.h > 0 && naturalSize.h > 0 ? displaySize.h / naturalSize.h : 1;

  const onCropPointerDown = (e: React.PointerEvent, action: 'move' | 'resize') => {
    e.preventDefault();
    e.stopPropagation();
    setCropDrag(action);
    cropStartRef.current = { x: e.clientX, y: e.clientY, rect: { ...cropRect } };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    if (!cropDrag) return;
    const onMove = (e: PointerEvent) => {
      const start = cropStartRef.current;
      const dx = (e.clientX - start.x) / scaleX;
      const dy = (e.clientY - start.y) / scaleY;
      if (cropDrag === 'move') {
        setCropRect((prev) => ({
          w: prev.w,
          h: prev.h,
          x: Math.max(0, Math.min(naturalSize.w - prev.w, start.rect.x + dx)),
          y: Math.max(0, Math.min(naturalSize.h - prev.h, start.rect.y + dy)),
        }));
      } else {
        setCropRect((prev) => ({
          x: prev.x,
          y: prev.y,
          w: Math.max(10, Math.min(naturalSize.w - prev.x, start.rect.w + dx)),
          h: Math.max(10, Math.min(naturalSize.h - prev.y, start.rect.h + dy)),
        }));
      }
    };
    const onUp = () => setCropDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [cropDrag, naturalSize.w, naturalSize.h, scaleX, scaleY]);

  return (
    <div className="dark h-[calc(100vh-4rem)] flex flex-col overflow-hidden bg-[#080a0f] text-slate-100">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 bg-[#0d1016] px-4 shadow-xl">
        <div className="flex items-center gap-2">
          <FilmIcon className="h-6 w-6 text-indigo-600" />
          <div>
            <h1 className="text-base font-semibold tracking-tight text-white">Prime Studio</h1>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">AI Video Editor</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" title="Zoom out">
            <MinusIcon className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700" title="Zoom in">
            <PlusIcon className="h-4 w-4" />
          </button>
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-2" />
          <button onClick={handleRender} disabled={!project || rendering} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            <ArrowDownTrayIcon className="h-4 w-4" />
            {rendering ? 'Rendering...' : 'Render'}
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="flex w-[292px] shrink-0 flex-col gap-6 overflow-y-auto border-r border-white/10 bg-[#0d1016] p-4">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FolderIcon className="h-5 w-5 text-indigo-500" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Selected Project</h2>
            </div>
            {selectedProject ? (
              <div className="p-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20">
                <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{selectedProject.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">Videos belong to this project</div>
              </div>
            ) : (
              <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-500">
                Please select a project from the top dropdown.
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project videos</h2>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || creating || !selectedProject}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <input ref={fileInputRef} type="file" accept="video/*" onChange={handleUpload} className="hidden" />
            </div>
            <input
              type="text"
              placeholder="Search videos..."
              value={videoSearch}
              onChange={(e) => setVideoSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white mb-2"
            />
            <div className="space-y-2">
              {mediaLoading && <p className="text-xs text-gray-500">Loading videos...</p>}
              {!mediaLoading && filteredProjectMedia.length === 0 && <p className="text-xs text-gray-500">No videos match your search. Upload one to start editing.</p>}
              {filteredProjectMedia.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleCreateFromMedia(item)}
                  disabled={creating}
                  className="w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-3 py-2 text-sm transition-colors disabled:opacity-50"
                >
                  <div className="font-medium truncate text-gray-800 dark:text-gray-200">{item.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{item.status} · {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Project audio</h2>
              <button
                onClick={() => audioFileInputRef.current?.click()}
                disabled={audioUploading || !project || !selectedProject}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                {audioUploading ? 'Uploading...' : 'Upload'}
              </button>
              <input ref={audioFileInputRef} type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" />
            </div>
            <input
              type="text"
              placeholder="Search audio..."
              value={audioSearch}
              onChange={(e) => setAudioSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm text-gray-900 dark:text-white mb-2"
            />
            <div className="space-y-2">
              {audioLoading && <p className="text-xs text-gray-500">Loading audio...</p>}
              {!audioLoading && filteredProjectAudio.length === 0 && <p className="text-xs text-gray-500">No audio found. Upload or generate audio in other tools.</p>}
              {filteredProjectAudio.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleAddAudioFromMedia(item)}
                  draggable={Boolean(project && item.url)}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'copy';
                    event.dataTransfer.setData('application/x-prime-audio-id', item.id || item._id);
                    event.dataTransfer.setData('text/plain', item.name);
                  }}
                  disabled={!project}
                  title={project ? 'Click to add at the playhead, or drag onto the timeline to create a new track' : 'Create an editor project first'}
                  className="w-full cursor-grab text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:border-emerald-400 dark:hover:border-emerald-600 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-3 py-2 text-sm transition-colors active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <div className="font-medium truncate text-gray-800 dark:text-gray-200">{item.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{item.status} · {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Stock media</h2>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => openStock('video')} className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2"><FilmIcon className="h-4 w-4" /> B-roll</button>
              <button onClick={() => openStock('music')} className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2"><MusicalNoteIcon className="h-4 w-4" /> Music</button>
            </div>
          </div>
        </aside>

        <main className="flex min-w-0 flex-1 flex-col bg-[#080a0f]">
          {loading && <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400"><ArrowPathIcon className="h-6 w-6 animate-spin mr-2" /> Loading project...</div>}

          {!loading && !project && (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
              <VideoCameraIcon className="h-16 w-16 mb-4 opacity-50" />
              <p>Select or create a project to start editing.</p>
            </div>
          )}

          {project && (
            <>
              <div className="hidden" aria-hidden="true">
                {timelineAudioSegments.map((segment) => timelineAudioSources[segment.segmentId] && (
                  <audio
                    key={segment.segmentId}
                    ref={(element) => { timelineAudioRefs.current[segment.segmentId] = element; }}
                    src={timelineAudioSources[segment.segmentId]}
                    preload="auto"
                  />
                ))}
              </div>
              <div ref={videoWrapperRef} className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_center,_#202536_0%,_#0c0f16_52%,_#080a0f_100%)] p-5">
                <div className="absolute left-5 top-4 z-10 flex items-center gap-2">
                  <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] font-medium text-slate-300 backdrop-blur">{project.width || naturalSize.w} × {project.height || naturalSize.h}</span>
                  <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[10px] font-medium text-slate-300 backdrop-blur">{project.fps || 30} FPS</span>
                </div>
                {videoSrc ? (
                  <>
                    {videoLoading && (
                      <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30 text-sm text-slate-300 backdrop-blur-sm">
                        <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" /> Loading preview...
                      </div>
                    )}
                    <div className="group relative flex max-h-full max-w-full items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-black shadow-[0_24px_80px_rgba(0,0,0,0.55)]" style={{ clipPath: previewClipPath }}>
                      <video
                        ref={videoRef}
                        src={videoObjectUrl || undefined}
                        className="max-h-[calc(100vh-480px)] min-h-[180px] max-w-full cursor-pointer object-contain"
                        onClick={togglePlay}
                        onTimeUpdate={(event) => {
                          const time = event.currentTarget.currentTime;
                          setCurrentTime(time);
                          syncTimelineAudio(time, !event.currentTarget.paused);
                        }}
                        onPlay={(event) => {
                          setIsPlaying(true);
                          syncTimelineAudio(event.currentTarget.currentTime, true);
                        }}
                        onPause={(event) => {
                          setIsPlaying(false);
                          syncTimelineAudio(event.currentTarget.currentTime, false);
                        }}
                        onEnded={(event) => {
                          setIsPlaying(false);
                          syncTimelineAudio(event.currentTarget.currentTime, false);
                        }}
                        onLoadedMetadata={(e) => {
                          const v = e.currentTarget;
                          const ns = { w: v.videoWidth || 1920, h: v.videoHeight || 1080 };
                          setMediaDuration(Number.isFinite(v.duration) ? v.duration : 0);
                          setNaturalSize(ns);
                          setCropRect({ x: 0, y: 0, w: ns.w, h: ns.h });
                          setVideoLoading(false);
                          updateDisplaySize();
                        }}
                        onError={() => {
                          setVideoLoading(false);
                          setIsPlaying(false);
                        }}
                      />
                      <button
                        onClick={togglePlay}
                        className={`absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white shadow-2xl backdrop-blur transition-all hover:scale-105 hover:bg-indigo-600 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}
                        aria-label={isPlaying ? 'Pause video' : 'Play video'}
                      >
                        {isPlaying ? <PauseIcon className="h-6 w-6" /> : <PlayIcon className="ml-1 h-6 w-6" />}
                      </button>
                      {activeLayers.map(({ track, segment }) => {
                        const effect = segment.effects?.[0];
                        if (track.type === 'caption') {
                          return (
                            <div key={segment.segmentId} className="pointer-events-none absolute inset-x-8 bottom-14 z-20 text-center">
                              <span className="inline rounded-md bg-black/75 px-2 py-1 text-lg font-bold leading-relaxed text-white shadow-lg [box-decoration-break:clone]">{effect?.params?.text || segment.title}</span>
                            </div>
                          );
                        }
                        const kind = effect?.params?.kind || 'image';
                        return (
                          <img
                            key={segment.segmentId}
                            src={segment.previewUrl || segment.s3Url}
                            alt={segment.title || kind}
                            className={`pointer-events-none absolute z-10 object-contain ${kind === 'watermark' ? 'right-4 top-4 max-h-[18%] max-w-[22%]' : 'left-1/2 top-1/2 max-h-[45%] max-w-[55%] -translate-x-1/2 -translate-y-1/2'}`}
                            style={{ opacity: effect?.params?.opacity ?? 1 }}
                          />
                        );
                      })}
                      <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/90 to-transparent px-4 pb-3 pt-8 opacity-0 transition-opacity group-hover:opacity-100">
                        <button onClick={togglePlay} className="text-white">{isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}</button>
                        <input type="range" min={0} max={Math.max(totalDuration, 0.01)} step={0.01} value={Math.min(currentTime, totalDuration)} onChange={(event) => seek(Number(event.target.value))} className="h-1 flex-1 accent-indigo-500" aria-label="Video position" />
                        <span className="font-mono text-[10px] text-slate-300">{formatTime(currentTime)}</span>
                      </div>
                    </div>
                    {tab === 'crop' && displaySize.w > 0 && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="relative" style={{ width: displaySize.w, height: displaySize.h }}>
                          <div
                            className="absolute border-2 border-indigo-500 bg-indigo-500/10 pointer-events-auto cursor-move"
                            style={{
                              left: cropRect.x * scaleX,
                              top: cropRect.y * scaleY,
                              width: cropRect.w * scaleX,
                              height: cropRect.h * scaleY,
                            }}
                            onPointerDown={(e) => onCropPointerDown(e, 'move')}
                          >
                            <div
                              className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-indigo-500 rounded-full cursor-se-resize"
                              onPointerDown={(e) => onCropPointerDown(e, 'resize')}
                            />
                          </div>
                          <div
                            className="absolute inset-0 bg-black/40 pointer-events-none"
                            style={{
                              clipPath: `polygon(0% 0%, 0% 100%, ${cropRect.x * scaleX}px 100%, ${cropRect.x * scaleX}px ${cropRect.y * scaleY}px, ${(cropRect.x + cropRect.w) * scaleX}px ${cropRect.y * scaleY}px, ${(cropRect.x + cropRect.w) * scaleX}px ${(cropRect.y + cropRect.h) * scaleY}px, ${cropRect.x * scaleX}px ${(cropRect.y + cropRect.h) * scaleY}px, ${cropRect.x * scaleX}px 100%, 100% 100%, 100% 0%)`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center text-gray-500 dark:text-gray-400">
                    <VideoCameraIcon className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>No preview source configured.</p>
                  </div>
                )}
              </div>

              <div className="h-[320px] min-h-[240px] shrink-0 border-t border-white/10">
                <EditorTimeline
                  tracks={project.timeline.tracks}
                  duration={totalDuration}
                  currentTime={currentTime}
                  zoom={zoom}
                  isPlaying={isPlaying}
                  selectedSegmentId={selectedSegment?.seg.segmentId || selectedSegmentId}
                  projectId={project.projectId}
                  audioSources={timelineAudioSources}
                  onSeek={seek}
                  onSelect={(segmentId, trackId) => {
                    setSelectedSegmentId(segmentId);
                    setSelectedTrackId(trackId);
                  }}
                  onMove={handleSegmentMove}
                  onTogglePlay={togglePlay}
                  onStep={stepTime}
                  onZoomChange={setZoom}
                  onSplit={handleSplit}
                  onDelete={handleDelete}
                  onAddAudioTrack={handleAddAudioTrack}
                  onDeleteTrack={handleDeleteTrack}
                  onAudioDrop={handleAudioDrop}
                />
              </div>
            </>
          )}
        </main>

        <aside className="w-[328px] shrink-0 overflow-y-auto border-l border-white/10 bg-[#0d1016] p-4">
          {!project && <p className="text-sm text-gray-500">No project selected.</p>}
          {project && (
            <>
              <div className="mb-5">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Inspector</div>
                <div className="flex gap-1 overflow-x-auto rounded-lg border border-white/10 bg-black/20 p-1">
                  {(['tools', 'layers', 'trim', 'crop', 'ai', 'stock'] as const).map((t) => (
                    <button
                      key={t}
                      onClick={() => setTab(t)}
                      className={`shrink-0 rounded-md px-2.5 py-1.5 text-[11px] font-medium capitalize transition-colors ${tab === t ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-950/40' : 'text-slate-400 hover:bg-white/10 hover:text-white'}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {tab === 'tools' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Playback</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={togglePlay} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">{isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}</button>
                    <button onClick={() => stepTime(-1)} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronLeftIcon className="h-5 w-5" /></button>
                    <button onClick={() => stepTime(1)} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"><ChevronRightIcon className="h-5 w-5" /></button>
                    <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{formatTime(currentTime)} / {formatTime(totalDuration)}</span>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 pt-2 border-t border-gray-200 dark:border-gray-700">Edit</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <ActionButton icon={<ScissorsIcon className="h-4 w-4" />} label="Split (S)" onClick={handleSplit} disabled={!selectedSegment} />
                    <ActionButton icon={<ArrowsPointingOutIcon className="h-4 w-4" />} label="Trim" onClick={handleTrim} disabled={!selectedSegment} />
                    <ActionButton icon={<SquaresPlusIcon className="h-4 w-4" />} label="Merge (M)" onClick={handleMerge} disabled={!selectedSegment} />
                    <ActionButton icon={<TrashIcon className="h-4 w-4" />} label="Delete" onClick={handleDelete} disabled={!selectedSegment} />
                  </div>

                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 pt-2 border-t border-gray-200 dark:border-gray-700">Transform</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <ActionButton icon={<PhotoIcon className="h-4 w-4" />} label="Crop" onClick={() => {
                      if (cropTargetSegment) setSelectedSegmentId(cropTargetSegment.segmentId);
                      setTab('crop');
                    }} disabled={!cropTargetSegment} />
                    <ActionButton icon={<MagnifyingGlassIcon className="h-4 w-4" />} label="Zoom" onClick={() => handleEffect('zoom', { speed: 0.002 })} disabled={!selectedSegment} />
                    <ActionButton icon={<SparklesIcon className="h-4 w-4" />} label="Reframe" onClick={() => handleEffect('reframe', { aspectRatio: '9:16' })} disabled={!selectedSegment} />
                    <ActionButton icon={<SwatchIcon className="h-4 w-4" />} label="Blur BG" onClick={() => handleEffect('backgroundBlur', { aspectRatio: '9:16' })} disabled={!selectedSegment} />
                  </div>

                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 pt-2 border-t border-gray-200 dark:border-gray-700">Audio</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <ActionButton icon={<SpeakerWaveIcon className="h-4 w-4" />} label="De-noise" onClick={() => handleEffect('noiseRemoval')} disabled={!selectedSegment} />
                    <ActionButton icon={<MusicalNoteIcon className="h-4 w-4" />} label="Silence" onClick={() => handleEffect('silenceRemoval')} disabled={!selectedSegment} />
                    <ActionButton icon={<SparklesIcon className="h-4 w-4" />} label="Voice" onClick={() => handleEffect('voiceEnhancement')} disabled={!selectedSegment} />
                    <ActionButton icon={<ScissorsIcon className="h-4 w-4" />} label="Jump cuts" onClick={() => handleEffect('jumpCutRemoval')} disabled={!selectedSegment} />
                  </div>

                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 pt-2 border-t border-gray-200 dark:border-gray-700">Visual</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <ActionButton icon={<SwatchIcon className="h-4 w-4" />} label="Color" onClick={() => handleEffect('colorCorrection')} disabled={!selectedSegment} />
                    <ActionButton icon={<SparklesIcon className="h-4 w-4" />} label="HDR" onClick={() => handleEffect('hdr')} disabled={!selectedSegment} />
                    <ActionButton icon={<ArrowPathIcon className="h-4 w-4" />} label="Transition" onClick={() => handleEffect('transition', { type: 'crossfade', duration: 0.5 })} disabled={!selectedSegment} />
                  </div>
                </div>
              )}

              {tab === 'layers' && (
                <div className="space-y-5">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-200"><ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-amber-300" /> Caption</div>
                    <textarea value={captionText} onChange={(event) => setCaptionText(event.target.value)} placeholder="Enter caption text" rows={3} className="w-full resize-none rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600 focus:border-indigo-500" />
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">Duration</label>
                      <input type="number" min={0.5} step={0.5} value={captionDuration} onChange={(event) => setCaptionDuration(Number(event.target.value))} className="w-20 rounded-md border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white" />
                      <button onClick={handleAddCaption} className="ml-auto rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-400">Add at playhead</button>
                    </div>
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-200"><PhotoIcon className="h-4 w-4 text-fuchsia-300" /> Images & watermark</div>
                    <div className="mb-3 flex items-center gap-2">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500">Duration</label>
                      <input type="number" min={0.5} step={0.5} value={overlayDuration} onChange={(event) => setOverlayDuration(Number(event.target.value))} className="w-20 rounded-md border border-white/10 bg-black/20 px-2 py-1.5 text-xs text-white" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => imageFileInputRef.current?.click()} disabled={imageUploading} className="rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/10 px-3 py-2 text-xs font-medium text-fuchsia-200 hover:bg-fuchsia-500/20 disabled:opacity-40">Upload image</button>
                      <button onClick={() => watermarkFileInputRef.current?.click()} disabled={imageUploading} className="rounded-lg border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-medium text-cyan-200 hover:bg-cyan-500/20 disabled:opacity-40">Add watermark</button>
                      <input ref={imageFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => handleImageUpload(event, 'image')} className="hidden" />
                      <input ref={watermarkFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => handleImageUpload(event, 'watermark')} className="hidden" />
                    </div>
                    {projectImages.length > 0 && (
                      <div className="mt-3 max-h-40 space-y-1.5 overflow-y-auto">
                        {projectImages.map((item) => (
                          <button key={item.id} onClick={() => item.url && addImageLayer(item.name, item.url, item.url, 'image')} disabled={!item.url} className="flex w-full items-center gap-2 rounded-md border border-white/10 px-2.5 py-2 text-left text-xs text-slate-300 hover:border-fuchsia-400/40 hover:bg-fuchsia-500/10 disabled:opacity-40">
                            <PhotoIcon className="h-4 w-4 shrink-0 text-fuchsia-300" /><span className="truncate">{item.name}</span><PlusIcon className="ml-auto h-3.5 w-3.5" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-white/10 pt-4">
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Current layers</div>
                    <div className="space-y-1.5">
                      {project.timeline.tracks.filter((track) => track.type === 'caption' || track.type === 'overlay').flatMap((track) => track.segments).map((segment) => (
                        <button key={segment.segmentId} onClick={() => setSelectedSegmentId(segment.segmentId)} className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs ${selectedSegmentId === segment.segmentId ? 'border-indigo-400 bg-indigo-500/15 text-white' : 'border-white/10 text-slate-400 hover:bg-white/5'}`}>
                          <span className="truncate">{segment.title}</span><span className="ml-auto font-mono text-[9px] text-slate-600">{formatTime(segment.timelineStart)}</span>
                        </button>
                      ))}
                      {project.timeline.tracks.every((track) => track.type !== 'caption' && track.type !== 'overlay') && <p className="text-xs text-slate-600">No visual layers yet.</p>}
                    </div>
                  </div>
                </div>
              )}

              {tab === 'trim' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Trim segment</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Source start (s)</label>
                      <input type="number" step={0.1} value={trimStart} onChange={(e) => setTrimStart(Number(e.target.value))} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Source end (s)</label>
                      <input type="number" step={0.1} value={trimEnd} onChange={(e) => setTrimEnd(Number(e.target.value))} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" />
                    </div>
                  </div>
                  <button onClick={handleTrim} className="w-full rounded-md bg-indigo-600 text-white px-3 py-2 text-sm font-medium hover:bg-indigo-700">Apply Trim</button>
                  <p className="text-xs text-gray-500">Tip: press S to split at the playhead, Space to play/pause, drag segments to move them.</p>
                </div>
              )}

              {tab === 'crop' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Crop with mouse</h3>
                  <p className="text-xs text-gray-500">Drag the box to move it. Drag the handle to resize. Then apply.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs text-gray-500 mb-1">X</label><input type="number" value={Math.round(cropRect.x)} onChange={(e) => setCropRect((r) => ({ ...r, x: Number(e.target.value) }))} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">Y</label><input type="number" value={Math.round(cropRect.y)} onChange={(e) => setCropRect((r) => ({ ...r, y: Number(e.target.value) }))} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">W</label><input type="number" value={Math.round(cropRect.w)} onChange={(e) => setCropRect((r) => ({ ...r, w: Number(e.target.value) }))} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" /></div>
                    <div><label className="block text-xs text-gray-500 mb-1">H</label><input type="number" value={Math.round(cropRect.h)} onChange={(e) => setCropRect((r) => ({ ...r, h: Number(e.target.value) }))} className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={resetCrop} className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Reset</button>
                    <button onClick={handleCrop} disabled={!cropTargetSegment} className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40">Apply Crop</button>
                  </div>

                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 pt-2 border-t border-gray-200 dark:border-gray-700">Zoom / Pan</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => handleEffect('zoom', { speed: 0.002 })} className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Slow Zoom</button>
                    <button onClick={() => handleEffect('pan', { direction: 'left', speed: 0.3 })} className="px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Pan Left</button>
                  </div>
                </div>
              )}

              {tab === 'ai' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">AI Effects</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <EffectButton label="AI Reframe 9:16" onClick={() => handleEffect('reframe', { aspectRatio: '9:16' })} />
                    <EffectButton label="AI Reframe 1:1" onClick={() => handleEffect('reframe', { aspectRatio: '1:1' })} />
                    <EffectButton label="Background Blur" onClick={() => handleEffect('backgroundBlur', { aspectRatio: '9:16' })} />
                    <EffectButton label="Object Remove" onClick={() => handleEffect('objectRemoval', { mask: 'auto' })} />
                    <EffectButton label="Noise Remove" onClick={() => handleEffect('noiseRemoval')} />
                    <EffectButton label="Silence Remove" onClick={() => handleEffect('silenceRemoval')} />
                    <EffectButton label="Voice Enhance" onClick={() => handleEffect('voiceEnhancement')} />
                    <EffectButton label="Jump Cut" onClick={() => handleEffect('jumpCutRemoval')} />
                    <EffectButton label="Color Correct" onClick={() => handleEffect('colorCorrection')} />
                    <EffectButton label="HDR" onClick={() => handleEffect('hdr')} />
                    <EffectButton label="Auto Transition" onClick={() => handleEffect('transition', { type: 'crossfade', duration: 0.5 })} />
                    <EffectButton label="Subtitle" onClick={() => handleEffect('subtitle', { style: 'standard' })} />
                  </div>
                </div>
              )}

              {tab === 'stock' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Stock Media</h3>
                  <div className="flex gap-2">
                    <button onClick={() => openStock('video')} className={`flex-1 px-3 py-2 rounded-md text-sm border ${stockType === 'video' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>Video</button>
                    <button onClick={() => openStock('music')} className={`flex-1 px-3 py-2 rounded-md text-sm border ${stockType === 'music' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300' : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'}`}>Music</button>
                  </div>
                  <form onSubmit={(e) => { e.preventDefault(); searchStock(stockType, stockQuery); }} className="flex gap-2">
                    <input type="text" value={stockQuery} onChange={(e) => setStockQuery(e.target.value)} placeholder="Search..." className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white" />
                    <button type="submit" className="px-3 py-2 rounded-md bg-gray-900 dark:bg-gray-600 text-white text-sm"><MagnifyingGlassIcon className="h-4 w-4" /></button>
                  </form>
                  {stockLoading && <p className="text-xs text-gray-500">Searching...</p>}
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {stockResults.map((item) => (
                      <div key={item.id} className="p-2 rounded-md border border-gray-200 dark:border-gray-700 text-sm flex items-center justify-between">
                        <div className="truncate pr-2">
                          <div className="font-medium text-gray-800 dark:text-gray-200">{item.title}</div>
                          <div className="text-xs text-gray-500">{item.source} · {item.duration ? `${item.duration}s` : 'N/A'}</div>
                        </div>
                        <button onClick={() => addStockToTimeline(item)} className="px-2 py-1 rounded bg-indigo-600 text-white text-xs hover:bg-indigo-700">Add</button>
                      </div>
                    ))}
                    {!stockLoading && stockResults.length === 0 && <p className="text-xs text-gray-500">No results. Search above.</p>}
                  </div>
                </div>
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

function ActionButton({ icon, label, onClick, disabled }: { icon: React.ReactNode; label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button onClick={onClick} disabled={disabled} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-indigo-400/40 hover:bg-indigo-500/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/[0.03]">
      {icon}
      {label}
    </button>
  );
}

function EffectButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left text-xs font-medium text-slate-300 transition-colors hover:border-indigo-400/40 hover:bg-indigo-500/10 hover:text-white">
      {label}
    </button>
  );
}

